import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { TFunction } from 'i18next';

import { Table, Tbody, Td, Th, Thead, Tr } from '../../../components/Table/Fake';
import { Attachment, Settlement } from '../types';
import { Money } from '../../../components/Formatters';
import Icon from '../../../components/Icons';
import { colors, distances, palette } from '../../../styles/constants';
import DateFormatter from '../../../components/Formatters/Date';
import { parseISOStrict } from '../../../helpers/date';
import { capitalize } from '../../../helpers/string';
import { formatDate, formatTime } from '../../../helpers/formatters';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { getCurrentLanguage } from '../../../i18n';
import { DestinationIdLabelMap } from '../../../util/usePayoutDestinations';
import { isExternalReport } from '../utils/settlementAttachments';
import TimeMachine from '../../../components/nucleoIcons/TimeMachine';
import { SalesLocation } from '../../../types/customers';

interface RowProps {
    row: Settlement;
    accountId: string;
    downloadFile: (accountId: string, filename: string, settlementId: string, attachmentId: string) => void;
    hasPayoutDestinationId: boolean;
    hasSourceAttachments: boolean;
    destinationIdLabelMap: DestinationIdLabelMap;
    hasStoreId: boolean;
    locationsByLocationId: Record<string, SalesLocation>;
}

const attachmentIconMap = new Map<string, 'xml' | 'pdf' | 'tabular' | 'excel'>([
    ['.xml', 'xml'],
    ['.pdf', 'pdf'],
    ['.xsl', 'tabular'],
    ['.xlsx', 'excel'],
    ['.xls', 'excel'],
    ['.csv', 'tabular'],
]);

interface AttachmentLinkProps {
    attachment: Attachment;
    download: (attachmentId: string, filename: string) => void;
}

const AttachmentLink = (
    {
        attachment,
        download,
    }: AttachmentLinkProps
) => {
    const { t } = useTranslation();
    const handleClick = () => {
        const pathElements = attachment.key.split('/');
        download(attachment.id, pathElements[pathElements.length - 1]);
    };
    const iconType = attachmentIconMap.get(attachment.extension);
    if (!iconType) {
        return null;
    }
    return (
        <AttachmentIcon
            onClick={handleClick}
            pointer={true}
            icon={iconType}
            title={t(`settlements.download${attachment.extension}`)}
        />
    );
};

const Row = (
    {
        row,
        accountId,
        downloadFile,
        hasPayoutDestinationId,
        hasSourceAttachments,
        destinationIdLabelMap,
        hasStoreId,
        locationsByLocationId,
    }: RowProps
) => {
    const { t } = useTranslation();
    const download = (attachmentId: string, filename: string) => {
        downloadFile(accountId, filename, row.id, attachmentId);
    };

    const payoutDestinationIdLabel = (row.payout_destination_id && destinationIdLabelMap[row.payout_destination_id]) || row.payout_destination_id;
    const storeIdLabel = (row.store_id && locationsByLocationId[row.store_id]?.name) || row.store_id;
    const filteredAttachments = (attachmentFilter: (item: Attachment) => boolean) =>
        row.attachments
            .filter(attachmentFilter)
            .sort((a, b) => b.extension.localeCompare(a.extension))
            .map((attachment) => (
                <AttachmentLink
                    key={attachment.id}
                    attachment={attachment}
                    download={download}
                />
            ));

    return (
        <Tr>
            <Td className="first-child">
                <DateFormatter isoString={row.settled_at || ''} />
            </Td>
            {row.amounts.length > 0 && (
                <Td className="align-right ellipsis">
                    <FlexColumnContent>
                        {row.amounts.map((amount) => (
                            <React.Fragment key={row.id + '-' + amount.currency}>
                                <Money amount={amount.amount} currency={amount.currency} boldCurrency />
                                <br />
                            </React.Fragment>
                        ))}
                        {row.payment_status === 'postponed' && <div title={t('settlements.payment_postponed')}><TimeMachine width="16px"/></div>}
                    </FlexColumnContent>
                </Td>
            )}
            {row.amounts.length === 0 && (
                <Td className="center ellipsis">
                    <FlexColumnContent>
                        <NoAmount title={t('settlements.no-transactions-for-period')}>-</NoAmount>
                        {row.payment_status === 'postponed' && <div title="Payment postponed"><TimeMachine width="16px"/></div>}
                    </FlexColumnContent>
                </Td>
            )}
            {row.amounts.length > 0 && (
                <Td className="align-right ellipsis">
                    {row.amounts.map((amount) => (
                        <React.Fragment key={row.id + '-' + amount.currency}>
                            <Money amount={amount.fee || 0} currency={amount.currency} boldCurrency />
                            <br />
                        </React.Fragment>
                    ))}
                </Td>
            )}
            {row.amounts.length === 0 && (
                <Td className="center">
                    <NoAmount title={t('settlements.no-transactions-for-period')}>-</NoAmount>
                </Td>
            )}
            <Td className="collapse-1200 ellipsis">{t(`settlements.providers.${row.provider}`)}</Td>
            <Td className="collapse-768 ellipsis">{row.provider_reference}</Td>
            <Td>{filteredAttachments((attachment) => attachment.created_by === 'dintero')}</Td>
            {hasSourceAttachments && <Td className="collapse-1200 ellipsis">{filteredAttachments(isExternalReport)}</Td>}
            {hasPayoutDestinationId && <Td className="collapse-1200 ellipsis">
                <span title={payoutDestinationIdLabel}>{payoutDestinationIdLabel}</span>
                {payoutDestinationIdLabel !== row.payout_destination_id && <>
                    <DestinationIdSubLabel title={row.payout_destination_id}>{row.payout_destination_id}</DestinationIdSubLabel>
                </>}
            </Td>}
            {hasStoreId && <Td className="collapse-1200 ellipsis">
                <span title={storeIdLabel}>{storeIdLabel}</span>
                {storeIdLabel !== row.store_id && <>
                    <DestinationIdSubLabel title={row.store_id}>{row.store_id}</DestinationIdSubLabel>
                </>}
            </Td>}
        </Tr>
    );
};

type Amounts = {
    amount: number;
    fee: number;
};
type GroupAmounts = {
    [currency: string]: Amounts;
};

interface SumRowProps {
    groupAmounts: GroupAmounts;
    accountId: string;
    label: string;
    downloadGroupCsv: () => void;
    downloadGroupCsvPdf: () => void;
    hasPayoutDestinationId: boolean;
    hasSourceAttachments: boolean;
    hasStoreId: boolean;
}

const SumRow = (
    {
        groupAmounts,
        label,
        downloadGroupCsv,
        downloadGroupCsvPdf,
        hasPayoutDestinationId,
        hasSourceAttachments,
        hasStoreId,
    }: SumRowProps
) => {
    const { t } = useTranslation();
    const amounts = Object.entries(groupAmounts).map(([key, value]) => ({ ...value, currency: key }));
    return (
        <SumTr>
            <Td className="first-child">
                <em>{t('settlements.fields.sum', { group: label })} </em>
            </Td>
            {amounts.length > 0 && (
                <Td className="align-right">
                    {amounts.map((amount) => (
                        <React.Fragment key={'summary-' + amount.currency}>
                            <Money amount={amount.amount} currency={amount.currency} boldCurrency />
                            <br />
                        </React.Fragment>
                    ))}
                </Td>
            )}
            {amounts.length === 0 && (
                <Td className="center">
                    <NoAmount title={t('settlements.no-transactions-for-period')}>-</NoAmount>
                </Td>
            )}
            {amounts.length > 0 && (
                <Td className="align-right">
                    {amounts.map((amount) => (
                        <React.Fragment key={'summary-' + amount.currency}>
                            <Money
                                key={'summary-' + amount.currency}
                                amount={amount.fee || 0}
                                currency={amount.currency}
                                boldCurrency
                            />
                            <br />
                        </React.Fragment>
                    ))}
                </Td>
            )}
            {amounts.length === 0 && (
                <Td className="center">
                    <NoAmount title={t('settlements.no-transactions-for-period')}>-</NoAmount>
                </Td>
            )}
            <Td className="collapse-1200 ellipsis"></Td>
            <Td className="collapse-768 ellipsis"></Td>
            <Td>
                <InvisibleButton onClick={downloadGroupCsvPdf}>
                    <AttachmentIcon pointer={true} icon={'pdf'} title={t(`settlements.download.csv`)} />
                </InvisibleButton>
                <InvisibleButton onClick={downloadGroupCsv}>
                    <AttachmentIcon pointer={true} icon={'tabular'} title={t(`settlements.download.csv`)} />
                </InvisibleButton>
            </Td>
            {hasSourceAttachments && <Td className="collapse-1200"></Td>}
            {hasPayoutDestinationId && <Td className="collapse-1200"></Td>}
            {hasStoreId && <Td className="collapse-1200"></Td>}
        </SumTr>
    );
};

export interface PayoutsTableProps {
    settlements: Settlement[][];
    accountId: string;
    downloadFile: (accountId: string, filename: string, settlementId: string, attachmentId: string) => void;
    createSettlementsCsv: (
        aid: string,
        settlements: Settlement[],
        t: TFunction,
        language: string,
        title: string | undefined,
        filename: string | undefined,
        payoutDestinationLabelMap: DestinationIdLabelMap | undefined,
    ) => void;
    createSettlementsCsvPdf: (
        aid: string,
        settlements: Settlement[],
        t: TFunction,
        language: string,
        title: string | undefined,
        filename: string | undefined,
        payoutDestinationLabelMap: DestinationIdLabelMap | undefined,
    ) => void;
    locationQueryParams: URLSearchParams;
    hasPayoutDestinationId: boolean;
    hasStoreId: boolean;
    hasSourceAttachments: boolean;
    destinationIdLabelMap: DestinationIdLabelMap;
    locations: SalesLocation[] | undefined;
}

const getLocalizedDay = (str: string) => format(parseISO(str), 'dd');

const getDateRange = (isoDatePart: string, locationQueryParams: URLSearchParams) => {
    const from = locationQueryParams.get('created_at.gte');
    const to = locationQueryParams.get('created_at.lte');
    if (from?.substring(0, 7) === isoDatePart && to?.substring(0, 7) === isoDatePart) {
        return `${getLocalizedDay(from)} - ${getLocalizedDay(to)} `;
    }
    const groupDate = parseISOStrict(`${isoDatePart}-02`);
    if (from?.substring(0, 7) === isoDatePart && groupDate) {
        return `${getLocalizedDay(from)} - ${endOfMonth(groupDate).getDate()} `;
    }
    if (to?.substring(0, 7) === isoDatePart) {
        return `01 - ${getLocalizedDay(to)} `;
    }
    return '';
};

const getGroupLabel = (group: Settlement[], locale: string, locationQueryParams: URLSearchParams) => {
    const isoDatePart = (group.find((elem) => elem)?.settled_at || '').substring(0, 7);
    try {
        const groupDate = parseISOStrict(`${isoDatePart}-02`); // using 2 here to avoid timezone trouble
        if (!groupDate) {
            throw new Error('Invalid group date');
        }
        const yearMonthString = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'long',
        }).format(groupDate);
        return `${getDateRange(isoDatePart, locationQueryParams)}${yearMonthString}`;
    } catch {
        return isoDatePart;
    }
};

const getGroupAmounts = (month: Settlement[]) =>
    month.reduce<GroupAmounts>((sums, settlement) => {
        const result = { ...sums };
        settlement.amounts.forEach((amountElem) => {
            const { amount, fee, currency } = amountElem;
            if (result[currency]) {
                result[currency].amount += amount;
                result[currency].fee += fee;
            } else {
                result[currency] = {
                    amount,
                    fee,
                };
            }
        });
        return result;
    }, {});

const SettlementsTable = (
    {
        settlements,
        accountId,
        downloadFile,
        createSettlementsCsv,
        createSettlementsCsvPdf,
        locationQueryParams,
        hasPayoutDestinationId,
        hasSourceAttachments,
        destinationIdLabelMap,
        hasStoreId,
        locations,
    }: PayoutsTableProps
) => {
    const { t, i18n } = useTranslation();
    const locale = getCurrentLanguage(i18n);
    const locationsByLocationId = useMemo(() => {
        if (!locations) {
            return {};
        }
        return locations.reduce((acc, item) => {
            if (!item.location_id) {
                return acc;
            }
            return {
                ...acc,
                [item.location_id]: item,
            };
        }, {} as Record<string, SalesLocation>);
    }, [locations]);
    return (
        <Table className="no-wrap">
            <Thead>
                <Tr>
                    <Th className="first-child">{t('settlements.fields.date')}</Th>
                    <Th className="center align-right">{t('settlements.fields.amount')}</Th>
                    <Th className="center align-right">{t('settlements.fields.fee')}</Th>
                    <Th className="collapse-1200 ellipsis">{t('settlements.fields.provider')}</Th>
                    <Th className="collapse-768 ellipsis">{t('settlements.fields.settlement_id')}</Th>
                    <Th>{t('settlements.fields.dintero_files')}</Th>
                    {hasSourceAttachments &&
                            <Th className="collapse-1200 ellipsis">{t('settlements.fields.source_files')}</Th>}
                    {hasPayoutDestinationId &&
                            <Th className="collapse-1200 ellipsis">{t('settlements.fields.payout_destination')}</Th>}
                    {hasStoreId && <Th className="collapse-1200 ellipsis">{t('settlements.fields.store_id')}</Th>}
                </Tr>
            </Thead>
            {settlements.length > 0 && (
                <>
                    {settlements.map((group, i) => {
                        const groupLabel = getGroupLabel(group, locale, locationQueryParams);
                        const amounts = getGroupAmounts(group);
                        const downloadGroupCsv = () => {
                            const now = new Date().toISOString();
                            const nowFormatted = `${formatDate(now, locale)}T${formatTime(now, locale)}`;
                            createSettlementsCsv(
                                accountId,
                                group,
                                t,
                                locale,
                                `Dintero settlements download ${accountId} ${groupLabel} downloaded at ${nowFormatted}`,
                                `${accountId}_${groupLabel}_Dintero-settlement`,
                                hasPayoutDestinationId ? destinationIdLabelMap : undefined
                            );
                        };
                        const downloadGroupCsvPdf = () => {
                            createSettlementsCsvPdf(
                                accountId,
                                group,
                                t,
                                getCurrentLanguage(i18n),
                                `Dintero settlements download ${accountId} ${groupLabel}`,
                                `${accountId}_${groupLabel}_Dintero-settlement`,
                                hasPayoutDestinationId ? destinationIdLabelMap : undefined
                            );
                        };
                        return (
                            <React.Fragment key={`group-${i}-${groupLabel}-${JSON.stringify(group)}`}>
                                <Tbody>
                                    <GroupTitle>
                                        <Td className="first-child">
                                            {capitalize(groupLabel)}
                                        </Td>
                                        <Td></Td>
                                        <Td></Td>
                                        <Td className="collapse-1200 ellipsis"></Td>
                                        <Td className="collapse-768 ellipsis"></Td>
                                        <Td></Td>
                                        {hasSourceAttachments && <Td className="collapse-1200 ellipsis" />}
                                        {hasPayoutDestinationId && <Td className="collapse-1200 ellipsis" />}
                                        {hasStoreId && <Td className="collapse-1200 ellipsis" />}
                                    </GroupTitle>
                                    {group.map((settlement) => (
                                        <Row
                                            key={`${settlement.provider}-${settlement.id}`}
                                            row={settlement}
                                            downloadFile={downloadFile}
                                            accountId={accountId}
                                            hasPayoutDestinationId={hasPayoutDestinationId}
                                            hasSourceAttachments={hasSourceAttachments}
                                            destinationIdLabelMap={destinationIdLabelMap}
                                            hasStoreId={hasStoreId}
                                            locationsByLocationId={locationsByLocationId}
                                        />
                                    ))}
                                    <SumRow
                                        groupAmounts={amounts}
                                        accountId={accountId}
                                        label={groupLabel}
                                        downloadGroupCsv={downloadGroupCsv}
                                        downloadGroupCsvPdf={downloadGroupCsvPdf}
                                        hasPayoutDestinationId={hasPayoutDestinationId}
                                        hasSourceAttachments={hasSourceAttachments}
                                        hasStoreId={hasStoreId}
                                    />
                                </Tbody>
                            </React.Fragment>
                        );
                    })}
                </>
            )}
        </Table>
    );
};

const GroupTitle = styled(Tr)`
    width: 100%;
    white-space: nowrap;
    & > span {
        background-color: ${palette.neutral[100]};
        color: ${palette.neutral[900]};
        font-size: 16px;
        font-weight: 600;
        padding-inline: var(--Spacing-1);
        padding-block: 8px;
    }
    position: sticky;
    top: 45px;
    z-index: 1;
`;

const NoAmount = styled.span`
    margin-left: 2.5em;
    display: inline-block;
    -webkit-transform: scale(3, 1); /* Safari and Chrome */
    -moz-transform: scale(3, 1); /* Firefox */
    -ms-transform: scale(3, 1); /* IE 9 */
    -o-transform: scale(3, 1); /* Opera */
    transform: scale(3, 1); /* W3C */
    color: ${colors.borderDark};
`;

const AttachmentIcon = styled(Icon)`
    margin-left: 2px;
    font-size: 1.3em;
    width: unset !important;
`;

const SumTr = styled(Tr)`
    & > span {
        font-weight: 600;
    }

    & > span.first-child:before {
        left: ${distances.normal};
    }

    & > span.last-child:before {
        right: ${distances.normal};
    }

    & > span.edge:before {
        display: none;
    }
`;

const InvisibleButton = styled.button`
    margin: 0;
    padding: 0;
    background: transparent;
    border: none;

    &:focus {
        outline: none;
    }
`;

const FlexColumnContent = styled.div`
    display: flex;
    justify-content: flex-end;

    > div {
      margin-left: ${distances.micro};
    }
`;

const DestinationIdSubLabel = styled.div`
    font-size: 0.8em;
    color: ${colors.textSecondary};
    overflow: hidden;
    text-overflow: ellipsis;
`;

export default SettlementsTable;
